import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { authService } from "../../services/api";
import { useTranslation } from "react-i18next";
import { PRODUCT_NAME } from "../../config/brand";
import StaffMfa from "../../components/auth/StaffMfa";

/**
 * /launch — where the LMS's "Online exams" links land (docs/22-LMS-CONTRACT.md
 * §2). Three visits, one page:
 *
 *   /launch?to=/student/exams   from the LMS: off to the LMS's sign-in (a
 *                               full-page navigation through our API's
 *                               /auth/oidc/start), which comes back to …
 *   /launch?code=…              … here, with a one-time code (60 s, once,
 *                               this browser only). Traded for the session,
 *                               kept as the sign-in sheet keeps it, then on to
 *                               the target the server allowed.
 *   /launch?error=…             the server said no: a plain message, and the
 *                               ordinary sign-in sheet one click away — once
 *                               in, the exam never depends on the LMS.
 *
 * The code is taken out of the address bar before anything else, so it is not
 * left in the history.
 */
// The callback's error codes (backend controllers/oidcController.js), each
// a sentence in the exam namespace: this is the first page an LMS candidate
// sees on exam day, in their language (W4.2).
const MESSAGES = {
  expired: "launch.error.expired",
  lms_refused: "launch.error.lmsRefused",
  lms_unavailable: "launch.error.lmsUnavailable",
  no_account: "launch.error.noAccount",
  account_inactive: "launch.error.accountInactive",
  // A student of another institution on the same LMS (the token's
  // institutions claim does not name this one, and the roster does not know them).
  not_enrolled: "launch.error.notEnrolled",
};
const FALLBACK = "launch.error.fallback";
// The exchange's refusal, by its code — never the server's English sentence.
const EXCHANGE_CODES = { LAUNCH_CODE_INVALID: "launch.error.expired" };

// Only a path of this app, never another site (the server allows only such).
const safePath = (to) => (typeof to === "string" && to.startsWith("/") && !to.startsWith("//") && !to.startsWith("/\\") ? to : "/student/exams");

export default function Launch() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation("exam");
  // The LMS is the first step only: a teacher who holds exam personas may
  // still owe their code where staff must give one (W3.1).
  const [mfa, setMfa] = useState(null);
  const ran = useRef(false);
  const params = new URLSearchParams(search);
  const [failure, setFailure] = useState(() => {
    const error = params.get("error");
    return error ? MESSAGES[error] || FALLBACK : "";
  });

  useEffect(() => {
    if (ran.current || failure) return;
    ran.current = true;
    const code = params.get("code");
    if (!code) {
      window.location.assign(authService.oidcStartUrl(params.get("to") || ""));
      return;
    }
    window.history.replaceState(null, "", "/launch");
    // A new sign-in replaces whatever this browser held: the old session's
    // token must not ride along on the exchange.
    localStorage.removeItem("token");
    authService
      .oidcExchange(code)
      .then(({ data }) => {
        if (data?.mfaRequired && data?.mfaToken) {
          setMfa(data);
          return;
        }
        login(data);
        navigate(safePath(data.to), { replace: true });
      })
      .catch((err) => {
        setFailure(EXCHANGE_CODES[err?.response?.data?.code] || FALLBACK);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (mfa && !failure) {
    return (
      <StaffMfa
        challenge={mfa}
        onDone={(session) => {
          login(session);
          navigate(safePath(session.to), { replace: true });
        }}
        onCancel={() => navigate("/login", { replace: true })}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-indigo-50 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-sm border border-indigo-100 p-8 text-center">
        <p className="text-sm font-semibold text-indigo-700">{PRODUCT_NAME}</p>
        {failure ? (
          <>
            <h1 className="mt-3 text-lg font-semibold text-slate-900">{t("launch.failedTitle")}</h1>
            <p className="mt-2 text-sm text-slate-600" role="alert">
              {t(failure)}
            </p>
            <Link
              to="/login"
              className="mt-6 inline-block rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              {t("launch.toSignIn")}
            </Link>
          </>
        ) : (
          <>
            <h1 className="mt-3 text-lg font-semibold text-slate-900">{t("launch.signingIn")}</h1>
            <p className="mt-2 text-sm text-slate-600">{t("launch.fromLms")}</p>
          </>
        )}
      </div>
    </div>
  );
}
